import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import { PlayFab } from './services/PlayFab'; // Assuming PlayFab service exists for CloudScript calls
import './SessionAnalysisPage.css'; // Create this CSS file
import { HandHistoryCard } from './components/HandHistoryCard'; // <<< Import Card Component
// <<< Import Chart and Cloud components >>>
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TagCloud } from 'react-tagcloud';

// Define the structure of the analysis object we expect

// <<< Define or Import parseCard helper >>>
const parseCard = (cardStr: string): { rank: string; suit: string } | null => {
    if (!cardStr || cardStr.length !== 2) return null;
    const rank = cardStr[0];
    const suit = cardStr[1].toLowerCase(); // Ensure suit is lowercase for consistency
    // Basic validation (can be expanded)
    const validRanks = '23456789TJQKA';
    const validSuits = 'cdhs';
    if (validRanks.includes(rank) && validSuits.includes(suit)) {
        return { rank, suit };
    }
    return null;
};
// <<< End parseCard helper >>>

interface HeroCards {
    holeCard1: string;
    holeCard2: string;
}

interface NotableHandCommentary {
    handId: string;
    commentary: string;
    heroCards?: HeroCards | null;
    board?: string[];
    heroProfitBB?: number;
    potSize?: number;
    overallRating?: number | null;
}

interface SessionAnalysis {
    sessionSummary: string;
    keyStrengths: string[];
    keyLeaks: string[];
    improvementSuggestions: string[];
    notableHandsCommentary: NotableHandCommentary[];
}

// <<< Define types for timeline and tag counts >>>
interface ProfitTimelinePoint {
    handIndex: number;
    cumulativeProfitBB: number;
}

interface TagCounts {
    custom: { [key: string]: number };
    leak: { [key: string]: number };
    strength: { [key: string]: number };
}

interface TagCloudData {
    value: string;
    count: number;
    // Optional: Add category for styling
    category?: 'custom' | 'leak' | 'strength';
}

// Corrected Interface: Contains an 'analysis' property
interface SessionAnalysisData { 
    analysis?: SessionAnalysis; // Make analysis optional or ensure it's always present
    totalHands?: number; // Keep other fields optional if they might be missing
    totalProfitBB?: number;
    calculatedStats?: { [key: string]: number }; // Contains raw counts and percentages
    preflopStats?: { [key: string]: number }; // May contain raw counts used for calculationStats
    postflopStats?: { [key: string]: number };
    aggregatedStats?: { [key: string]: number }; // Keep if it contains other useful raw counts
    PK?: string;
    SK?: string;
    averageStreetRatings?: { [key: string]: number | null };
    averageOverallRating?: number;
    // <<< Add profitTimeline and tagCounts >>>
    profitTimeline?: ProfitTimelinePoint[];
    tagCounts?: TagCounts;
    tags?: { [key: string]: string[] }; // Keep if needed, but tagCounts might be better
    createdAt?: string;
    updatedAt?: string;
    userId?: string;
    sessionId?: string; // Add sessionId if it's part of the data item
    exists?: boolean;   // Add exists if it's part of the data item
    success?: boolean; // Add success if it's part of the data item
}

// <<< ADD Helper function to get rating category class (similar to AnalysisDisplay) >>>
const getRatingCategoryClass = (rating: number | undefined | null): string => {
    if (rating === undefined || rating === null) return 'rating-unknown';
    if (rating >= 9) return 'rating-excellent'; // 9-10
    if (rating >= 7) return 'rating-good';      // 7-8
    if (rating >= 4) return 'rating-okay';       // 4-6
    if (rating >= 1) return 'rating-poor';       // 1-3
    return 'rating-unknown'; // Fallback
};
// <<< END Helper >>>

const SessionAnalysisPage: React.FC = () => {
    const { playfabId, sessionId } = useParams<{ playfabId: string, sessionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    // State for the analysis data
    const [analysisData, setAnalysisData] = useState<SessionAnalysisData | null>(location.state?.analysisData || null);
    const [isLoading, setIsLoading] = useState<boolean>(!analysisData);
    const [error, setError] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<string>("Share Analysis"); // <<< ADD State for button text

    useEffect(() => {
        // Fetch data only if it wasn't passed via route state (e.g., page refresh)
        if (!analysisData && playfabId && sessionId) {
            setIsLoading(true);
            setError(null);
            console.log(`SessionAnalysisPage: Fetching analysis data for session ${sessionId}, user ${playfabId}`);

            const fetchAnalysis = async () => {
                try {
                    const result = await PlayFab.executeCloudScript('getDynamoDBData', {
                        action: 'getSessionAnalysisData',
                        ownerPlayfabId: playfabId,
                        sessionId: sessionId
                    });

                    const nestedResult = result.data?.FunctionResult?.FunctionResult;
                    console.log("Raw nestedResult from getSessionAnalysisData CloudScript:", nestedResult); // Add log

                    // Check if the overall CloudScript call and the nested Lambda call were successful
                    if (result.success && nestedResult?.success) {
                        // --- CORRECTED ACCESS --- 
                        const responseData = nestedResult.data; // Get the { exists, analysis } object
                        console.log("Parsed responseData from nestedResult:", responseData); // Add log
                        
                        if (responseData?.exists === true) {
                            // Now we expect the whole item including stats and the structured analysis
                            setAnalysisData(responseData); 
                            console.log("Full analysis data found and set:", responseData);
                        } else {
                            // Analysis does not exist according to the backend
                            console.log("Backend reported analysis does not exist.");
                            setError("Analysis data not found for this session."); 
                        }
                        // --- End Correction --- 
                    } else {
                        // Handle CloudScript or Lambda error
                        const errorMessage = nestedResult?.error || 'Failed to fetch analysis data';
                        console.error("Error fetching analysis data (CloudScript/Lambda failure):", errorMessage);
                        setError(errorMessage);
                    }
                } catch (err) {
                    console.error("Error fetching session analysis (Network/SDK Error):", err);
                    setError(err instanceof Error ? err.message : "An unknown error occurred while fetching analysis.");
                } finally {
                    setIsLoading(false);
                }
            };

            fetchAnalysis();
        } else if (!playfabId || !sessionId) {
            setError("Missing user or session ID in URL.");
            setIsLoading(false);
        }
        // If analysisData was passed via state initially, we don't need to fetch.
        // The dependency array ensures this runs only when needed.
    }, [analysisData, playfabId, sessionId]); // Re-run if IDs change or if analysisData was initially null

    const renderList = (title: string, items: string[], sectionClass: string) => {
        if (!items || items.length === 0) return null;
        return (
            <div className={`analysis-section ${sectionClass}`}>
                <h3>{title}</h3>
                <ul>
                    {items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            </div>
        );
    };

    // Helper to format stat numbers
    const formatStat = (value: number | undefined): string => {
        if (value === undefined || value === null) return 'N/A';
        // Add specific formatting if needed (e.g., percentages)
        return value.toFixed(2); 
    };

    // Helper to format stat keys into readable labels
    const formatStatKey = (key: string): string => {
        // Simple conversion: camelCase/lowercase to Title Case with spaces
        // Example: flopCbet -> Flop Cbet, vpip -> Vpip
        const result = key.replace(/([A-Z])/g, ' $1'); // Add space before capitals
        return result.charAt(0).toUpperCase() + result.slice(1);
    };

    // Helper function to render a block of stats, now categorized
    const renderStatsBlock = (statsObject: { [key: string]: number } | undefined, title: string) => {
        if (!statsObject || Object.keys(statsObject).length === 0) {
            return null;
        }

        const preflopKeys = ['vpip', 'pfr', 'threeBet'];
        const postflopKeys = ['flopCbet', 'turnCbet', 'riverCbet', 'foldToFlopCbet', 'foldToTurnCbet', 'foldToRiverCbet'];

        const preflopStats: { [key: string]: number } = {};
        const postflopStats: { [key: string]: number } = {};

        Object.entries(statsObject).forEach(([key, value]) => {
            const baseKey = key.replace('Percent', '');
            if (preflopKeys.includes(baseKey)) {
                preflopStats[key] = value;
            } else if (postflopKeys.includes(baseKey)) {
                postflopStats[key] = value;
            } else {
                // Handle any stats that don't fit? For now, ignore them in categorized view
                // Or add an 'otherStats' category if needed.
                console.warn(`Stat key "${key}" not categorized.`);
            }
        });

        // Function to chunk array into smaller arrays
        const chunkArray = <T,>(array: T[], size: number): T[][] => {
            const result: T[][] = [];
            for (let i = 0; i < array.length; i += size) {
                result.push(array.slice(i, i + size));
            }
            return result;
        };

        // Helper to render a single category of stats
        const renderCategory = (categoryTitle: string, categoryStats: { [key: string]: number }, categoryClass: string) => {
            const entries = Object.entries(categoryStats);
            if (entries.length === 0) return null;

            const chunkedStats = chunkArray(entries, 4);

            return (
                <div className={`stats-category ${categoryClass}`}>
                    <h4>{categoryTitle}</h4>
                    {chunkedStats.map((chunk, chunkIndex) => (
                        <div key={`stat-chunk-${categoryTitle}-${chunkIndex}`} className="overview-stats-row">
                            {chunk.map(([key, value]) => {
                                const displayKey = key.endsWith('Percent') ? key.replace('Percent', '') : key;
                                const isPercentage = key.endsWith('Percent');
                                return (
                                    <span key={key} className="stat-item">
                                        <strong>{formatStatKey(displayKey)}</strong>
                                        {formatStat(value)}{isPercentage ? '%' : ''}
                                    </span>
                                );
                            })}
                        </div>
                    ))}
                </div>
            );
        };

        return (
            <div className="analysis-section stats-block">
                <h3>{title}</h3>
                {renderCategory("Preflop Stats", preflopStats, "preflop-stats")}
                {renderCategory("Postflop Stats", postflopStats, "postflop-stats")}
            </div>
        );
    };

    // <<< Helper to prepare data for TagCloud (Now returns TWO arrays) >>>
    const prepareTagCloudData = (tagCounts: TagCounts | undefined): { leakTags: TagCloudData[], strengthTags: TagCloudData[] } => {
        const leakTags: TagCloudData[] = [];
        const strengthTags: TagCloudData[] = []; // Only strengths now

        if (tagCounts) {
            Object.entries(tagCounts.custom || {}).forEach(([tag, count]) => {
                leakTags.push({ value: tag, count: count, category: 'custom' });
            });
            Object.entries(tagCounts.leak || {}).forEach(([tag, count]) => {
                leakTags.push({ value: tag, count: count, category: 'leak' });
            });
            Object.entries(tagCounts.strength || {}).forEach(([tag, count]) => {
                strengthTags.push({ value: tag, count: count, category: 'strength' });
            });
        }

        // Optional: Sort arrays if needed
        // leakTags.sort((a, b) => b.count - a.count);
        // strengthTags.sort((a, b) => b.count - a.count);

        return { leakTags, strengthTags };
    };

     // <<< Custom renderer for tags (Can be simplified if CSS handles all styling) >>>
    const tagRenderer = (tag: TagCloudData, size: number, color: string) => {
        const categoryClass = tag.category ? `tag-${tag.category}` : '';
        return (
            <span key={tag.value} style={{ fontSize: `${size}px`, color: color, margin: '3px', padding: '3px' }} className={`tag-cloud-tag ${categoryClass}`}>
                {tag.value}
            </span>
        );
    };

    // <<< ADD Share Handler >>>
    const handleShareAnalysis = () => {
        if (!playfabId || !sessionId) return;
        // Construct the shareable URL - **NOTE:** We need a route/page for this URL
        // For now, let's assume a route like `/shared-analysis/:playfabId/:sessionId` exists
        const shareUrl = `${window.location.origin}/shared-analysis/${playfabId}/${sessionId}`;

        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                setCopyStatus("Link Copied!");
                setTimeout(() => setCopyStatus("Share Analysis"), 2000); // Reset after 2s
            })
            .catch(err => {
                console.error('Failed to copy share URL: ', err);
                setCopyStatus("Copy Failed");
                // Maybe show an alert or different feedback
                setTimeout(() => setCopyStatus("Share Analysis"), 2000);
            });
    };
    // <<< END Share Handler >>>

    return (
        <div className="session-analysis-page">
            {/* <<< Button Container >>> */}
            <div className="page-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <button onClick={() => navigate(`/history/user/${playfabId}/sessions/${sessionId}`)} className="back-button">
                    &larr; Back to Session Hands
                </button>
                {/* <<< ADD Share Button >>> */}
                {analysisData && !isLoading && !error && (
                    <button onClick={handleShareAnalysis} className="share-button" style={{ /* Add styling later */ }}>
                        {copyStatus}
                    </button>
                )}
            </div>

            <h2>Session Analysis: {sessionId}</h2>

            {isLoading ? (
                <div className="loading">Loading analysis...</div>
            ) : error ? (
                <div className="error">Error: {error}</div>
            ) : analysisData ? (
                <div className="analysis-dashboard">
                    {/* --- Session Overview Section (Now simpler) --- */}
                    <div className="analysis-section session-overview"> 
                        <h3>Session Overview</h3>
                        <div className="overview-stats"> 
                            {/* Display just the key overall numbers here */}
                            <span><strong>Total Hands</strong> {analysisData.totalHands ?? 'N/A'}</span>
                            {/* <<< Apply color based on profit >>> */}
                            <span className={analysisData.totalProfitBB === undefined || analysisData.totalProfitBB === null ? '' : (analysisData.totalProfitBB >= 0 ? 'profit-positive' : 'profit-negative')}>
                                <strong>Total Profit (BB)</strong> 
                                {analysisData.totalProfitBB === undefined || analysisData.totalProfitBB === null ? 'N/A' : `${analysisData.totalProfitBB >= 0 ? '+' : ''}${analysisData.totalProfitBB.toFixed(2)}`}
                            </span>
                            {/* <<< Apply rating class >>> */}
                            <span className={getRatingCategoryClass(analysisData.averageOverallRating)}>
                                <strong>Avg Overall Rating</strong>
                                {formatStat(analysisData.averageOverallRating)}
                            </span>
                            {/* Maybe add average street ratings if desired */}
                            {analysisData.averageStreetRatings && Object.entries(analysisData.averageStreetRatings)
                                .filter(([_, value]) => value !== null) // Only show streets that were rated
                                .map(([street, rating]) => (
                                     /* <<< Apply rating class >>> */
                                    <span key={street} className={getRatingCategoryClass(rating)}>
                                        <strong>Avg {street.charAt(0).toUpperCase() + street.slice(1)} Rating</strong>
                                        {formatStat(rating as number)}
                                    </span>
                            ))}
                        </div>
                    </div>
                    {/* --- End Overview --- */}

                    {/* --- Profit Timeline Chart --- */}
                    {analysisData.profitTimeline && analysisData.profitTimeline.length > 1 && (
                        <div className="analysis-section profit-timeline-section">
                            <h3>Profit Timeline (Cumulative BB)</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart
                                    data={analysisData.profitTimeline}
                                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#4a5568" />
                                    <XAxis dataKey="handIndex" stroke="#a0aec0" label={{ value: 'Hand Number', position: 'insideBottomRight', offset: -5, fill:'#a0aec0' }} />
                                    <YAxis stroke="#a0aec0" label={{ value: 'Profit (BB)', angle: -90, position: 'insideLeft', fill:'#a0aec0' }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#2d3748', border: '1px solid #4a5568' }}
                                        labelStyle={{ color: '#e2e8f0' }}
                                    />
                                    <Legend wrapperStyle={{ color: '#e2e8f0' }} />
                                    <Line type="monotone" dataKey="cumulativeProfitBB" name="Cumulative Profit" stroke="#63b3ed" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    {/* --- End Profit Timeline --- */}

                    {/* --- Analysis Text Sections --- */}
                    <div className="analysis-section summary">
                        <h3>Session Summary</h3>
                        <p>{analysisData.analysis?.sessionSummary}</p>
                    </div>

                    {/* <<< REORDERED: Leaks -> Suggestions -> Strengths >>> */}
                    {renderList("Key Leaks", analysisData.analysis?.keyLeaks ?? [], "key-leaks-section")}
                    {renderList("Improvement Suggestions", analysisData.analysis?.improvementSuggestions ?? [], "improvement-suggestions-section")}
                    {renderList("Key Strengths", analysisData.analysis?.keyStrengths ?? [], "key-strengths-section")}
                    
                    {/* --- Updated Notable Hands Commentary Section --- */}
                    <div className="analysis-section commentary key-hands-section">
                        <h3>Notable Hands Commentary</h3>
                        {analysisData.analysis?.notableHandsCommentary && analysisData.analysis.notableHandsCommentary.length > 0 ? (
                            <ul className="notable-hands-list">
                                {analysisData.analysis.notableHandsCommentary.map((handComment, index) => {
                                    // Parse Hero Cards
                                    let heroCardsDisplay: (React.ReactNode | null)[] = [null, null];
                                    if (handComment.heroCards) {
                                        const card1 = parseCard(handComment.heroCards.holeCard1);
                                        const card2 = parseCard(handComment.heroCards.holeCard2);
                                        heroCardsDisplay = [
                                            card1 ? <HandHistoryCard key={handComment.heroCards.holeCard1} rank={card1.rank} suit={card1.suit} /> : null,
                                            card2 ? <HandHistoryCard key={handComment.heroCards.holeCard2} rank={card2.rank} suit={card2.suit} /> : null
                                        ];
                                    }
                                    // Parse Board Cards
                                    let boardCardsDisplay: (React.ReactNode | null)[] = [];
                                    if (handComment.board && Array.isArray(handComment.board)) {
                                        boardCardsDisplay = handComment.board.map((cardStr: string) => {
                                            const card = parseCard(cardStr);
                                            return card ? <HandHistoryCard key={cardStr} rank={card.rank} suit={card.suit} /> : null;
                                        });
                                    }

                                    return (
                                        <li key={index} className="notable-hand-item">
                                            <div className="notable-hand-header">
                                                <Link to={`/history/user/${playfabId}/hand/${handComment.handId}`} className="hand-link">
                                                    Hand {handComment.handId}
                                                </Link>
                                                {/* <<< Apply color based on profit >>> */}
                                                <span className={`notable-hand-profit ${handComment.heroProfitBB === undefined || handComment.heroProfitBB === null ? '' : (handComment.heroProfitBB >= 0 ? 'profit-positive' : 'profit-negative')}`}>
                                                    Result: {handComment.heroProfitBB === undefined || handComment.heroProfitBB === null ? 'N/A' : `${handComment.heroProfitBB >= 0 ? '+' : ''}${handComment.heroProfitBB.toFixed(2)} BB`}
                                                </span>
                                            </div>
                                            <div className="notable-hand-details">
                                                {/* Hero Cards */}
                                                <div className="card-group hero-cards-group">
                                                    <strong>Hero:</strong>
                                                    {handComment.heroCards ? (
                                                        <span className="notable-hand-cards">
                                                            {heroCardsDisplay[0]} {heroCardsDisplay[1]}
                                                        </span>
                                                    ) : (
                                                         <span>--</span>
                                                    )}
                                                </div>
                                                {/* Board Cards */}
                                                {boardCardsDisplay.length > 0 && (
                                                    <div className="card-group board-cards-group">
                                                        <strong>Board:</strong>
                                                        <span className="notable-hand-cards">
                                                             {boardCardsDisplay}
                                                        </span>
                                                    </div>
                                                )}
                                                 {/* Pot Size */}
                                                {handComment.potSize !== null && handComment.potSize !== undefined && (
                                                    <div className="pot-size-info">
                                                         <strong>Pot:</strong> ${handComment.potSize.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                            <p className="notable-hand-commentary">
                                                {handComment.commentary}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p>No specific hands highlighted in this analysis.</p>
                        )}
                    </div>
                    {/* --- End Commentary --- */}

                    {/* --- Dynamically Rendered Stat Blocks --- */}
                    {renderStatsBlock(analysisData.calculatedStats, "Key Session Stats")}

                    {/* --- Tag Cloud Section (Now potentially two clouds) --- */}
                    {analysisData.tagCounts && (Object.keys(analysisData.tagCounts.custom || {}).length > 0 || Object.keys(analysisData.tagCounts.leak || {}).length > 0 || Object.keys(analysisData.tagCounts.strength || {}).length > 0) && (() => {
                        const { leakTags, strengthTags } = prepareTagCloudData(analysisData.tagCounts);
                        return (
                            <div className="analysis-section tag-cloud-section">
                                {leakTags.length > 0 && (
                                    <div className="tag-cloud-container leak-cloud-container">
                                        <h4>Leak Tags</h4>
                                        <TagCloud
                                            minSize={14}
                                            maxSize={35}
                                            tags={leakTags}
                                            renderer={tagRenderer}
                                        />
                                    </div>
                                )}
                                {strengthTags.length > 0 && (
                                    <div className="tag-cloud-container strength-cloud-container">
                                        <h4>Strength Tags</h4>
                                        <TagCloud
                                            minSize={14}
                                            maxSize={35}
                                            tags={strengthTags}
                                            renderer={tagRenderer}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    {/* --- End Tag Cloud --- */}
                </div>
            ) : (
                <div className="loading">Waiting for analysis data...</div>
            )}
        </div>
    );
};

export default SessionAnalysisPage; 